import { useEffect, useState } from "react";
import { X, Sparkles, Loader2, ArrowRight, Check } from "lucide-react";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BASE}/api`;

const client = axios.create({ baseURL: API, validateStatus: () => true, withCredentials: true });

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-[10px] tracking-[0.25em] uppercase border px-3 py-2 transition-colors duration-300 ${
        active
          ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))] text-[hsl(var(--bg))]"
          : "border-[hsl(var(--line))] text-[hsl(var(--ink))] hover:border-[hsl(var(--ink))]"
      }`}
    >
      {children}
    </button>
  );
}

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function MoodBoardModal({ open, onClose }) {
  const [presets, setPresets] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [eventType, setEventType] = useState("wedding");
  const [colors, setColors] = useState("blush-gold");
  const [style, setStyle] = useState("whimsical");
  const [freeform, setFreeform] = useState("");
  const [board, setBoard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Follow-up note flow (after board renders)
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteSent, setNoteSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    client.get("/mood-board/config/presets").then((r) => {
      if (r.status === 200) setPresets(r.data);
    });
  }, [open]);

  const reset = () => {
    setBoard(null);
    setNoteOpen(false);
    setNote("");
    setNoteSent(false);
    setError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canGenerate = name.trim().length >= 2 && isValidEmail(email.trim()) && !busy;

  const generate = async () => {
    if (!canGenerate) return;
    setBusy(true);
    setError("");
    setBoard(null);
    const res = await client.post("/mood-board/generate", {
      name: name.trim(),
      email: email.trim(),
      event_type: eventType,
      colors,
      style,
      freeform: freeform.trim().slice(0, 200),
    });
    setBusy(false);
    if (res.status === 503) {
      setError(
        res.data?.detail ||
          "The Vision Studio is temporarily unavailable. Please contact us directly for a bespoke design consultation.",
      );
      return;
    }
    if (res.status === 429) {
      setError(res.data?.detail || "Daily limit reached. Please book a consultation for more.");
      return;
    }
    if (res.status >= 400) {
      setError(res.data?.detail || "Something went wrong. Please try again in a moment.");
      return;
    }
    setBoard(res.data);
  };

  const sendNote = async () => {
    if (!board) return;
    setNoteBusy(true);
    const res = await client.post(`/mood-board/${board.id}/save`, {
      name: name.trim(),
      email: email.trim(),
      note: note.trim(),
    });
    setNoteBusy(false);
    if (res.status >= 400) {
      setError(res.data?.detail || "Could not send. Please try again.");
      return;
    }
    setNoteSent(true);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-2 md:p-6"
      onClick={handleClose}
      data-testid="mood-board-overlay"
    >
      <div
        className="bg-[hsl(var(--bg))] w-full max-w-5xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="mood-board-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[hsl(var(--line))]">
          <div>
            <div className="kicker text-[hsl(var(--accent))]">Vision Studio</div>
            <div className="font-display text-2xl md:text-3xl mt-1">
              Describe your <span className="italic">vibe</span>
            </div>
          </div>
          <button onClick={handleClose} aria-label="Close" data-testid="mood-board-close">
            <X size={18} />
          </button>
        </div>

        {/* Input form */}
        {!board && (
          <div className="p-6 md:p-8 space-y-6">
            <p className="text-sm text-[hsl(var(--ink-soft))] max-w-2xl">
              Share your name & email, pick your event, palette and style — Shubhra will personally
              review the concept board Ayaan generates and follow up within 48 hours.
            </p>

            <div className="grid md:grid-cols-2 gap-3">
              <input
                data-testid="mood-input-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="bg-transparent border border-[hsl(var(--line))] p-3 text-sm outline-none focus:border-[hsl(var(--ink))]"
              />
              <input
                data-testid="mood-input-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                type="email"
                className="bg-transparent border border-[hsl(var(--line))] p-3 text-sm outline-none focus:border-[hsl(var(--ink))]"
              />
            </div>

            <div>
              <div className="kicker mb-3">Event type</div>
              <div className="flex flex-wrap gap-2">
                {(presets?.event_types || []).map((et) => (
                  <Chip
                    key={et.id}
                    testid={`chip-event-${et.id}`}
                    active={eventType === et.id}
                    onClick={() => setEventType(et.id)}
                  >
                    {et.label}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <div className="kicker mb-3">Color palette</div>
              <div className="flex flex-wrap gap-2">
                {(presets?.colors || []).map((c) => (
                  <Chip
                    key={c.id}
                    testid={`chip-color-${c.id}`}
                    active={colors === c.id}
                    onClick={() => setColors(c.id)}
                  >
                    {c.label}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <div className="kicker mb-3">Style</div>
              <div className="flex flex-wrap gap-2">
                {(presets?.styles || []).map((s) => (
                  <Chip
                    key={s.id}
                    testid={`chip-style-${s.id}`}
                    active={style === s.id}
                    onClick={() => setStyle(s.id)}
                  >
                    {s.label}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <div className="kicker mb-3">
                Anything specific?{" "}
                <span className="lowercase tracking-normal opacity-60">
                  (optional, max 200 chars)
                </span>
              </div>
              <textarea
                data-testid="mood-freeform"
                rows={3}
                maxLength={200}
                value={freeform}
                onChange={(e) => setFreeform(e.target.value)}
                placeholder="e.g. outdoor garden with lantern lighting, or bright bollywood cinema vibe"
                className="w-full bg-transparent border border-[hsl(var(--line))] p-3 text-sm outline-none focus:border-[hsl(var(--ink))]"
              />
            </div>

            {error && (
              <div className="text-xs text-[hsl(var(--accent))] border border-[hsl(var(--accent))] p-3">
                {error}
              </div>
            )}

            <div className="pt-2 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-[10px] tracking-[0.25em] uppercase opacity-60">
                Every board is reviewed personally by Shubhra
              </p>
              <button
                onClick={generate}
                disabled={!canGenerate}
                data-testid="mood-generate-btn"
                className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-6 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--accent))] transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                {busy ? "Generating (~15s)…" : "Generate Board"}
              </button>
            </div>
          </div>
        )}

        {/* Results view */}
        {board && (
          <div className="p-6 md:p-8 space-y-8">
            <div className="grid md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <div className="kicker">The vibe</div>
                <div className="font-display text-xl md:text-2xl mt-2 italic leading-snug">
                  &ldquo;{board.caption || "A signature mood, curated for your celebration."}&rdquo;
                </div>
              </div>
              {board.palette?.length > 0 && (
                <div>
                  <div className="kicker mb-3">Palette</div>
                  <div className="flex gap-2">
                    {board.palette.map((hex, i) => (
                      <div key={`${hex}-${i}`} className="flex-1 text-center">
                        <div
                          className="w-full aspect-square border border-[hsl(var(--line))]"
                          style={{ backgroundColor: hex }}
                        />
                        <div className="text-[9px] tracking-widest mt-1 opacity-70">{hex}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {board.images.map((img, i) => (
                <div key={`${img.slot}-${i}`} className="overflow-hidden">
                  <img
                    src={img.data_uri}
                    alt={img.slot}
                    className="w-full aspect-[4/3] object-cover"
                  />
                  <div className="text-[9px] tracking-[0.3em] uppercase opacity-60 mt-1">
                    {img.slot}
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-[hsl(var(--line))] p-4 text-xs text-[hsl(var(--ink-soft))]">
              <Check size={14} className="inline mr-2 text-[hsl(var(--accent))]" />
              Board saved. Shubhra has been notified and will follow up at{" "}
              <strong className="text-[hsl(var(--ink))]">{email}</strong> within 48 hours.
            </div>

            {!noteSent && !noteOpen && (
              <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
                <button
                  onClick={() => reset()}
                  data-testid="mood-regen-btn"
                  className="text-[11px] tracking-[0.3em] uppercase border-b border-[hsl(var(--ink))] pb-1"
                >
                  Try another vibe
                </button>
                <button
                  onClick={() => setNoteOpen(true)}
                  data-testid="mood-note-cta"
                  className="inline-flex items-center gap-2 border border-[hsl(var(--ink))] px-5 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--ink))] hover:text-[hsl(var(--bg))] transition-colors duration-500"
                >
                  Add a note for Shubhra <ArrowRight size={12} />
                </button>
              </div>
            )}

            {noteOpen && !noteSent && (
              <div className="pt-4 border-t border-[hsl(var(--line))] space-y-3">
                <textarea
                  data-testid="mood-note-text"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Event date, guest count, questions…"
                  className="w-full bg-transparent border border-[hsl(var(--line))] p-3 text-sm outline-none focus:border-[hsl(var(--ink))]"
                />
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setNoteOpen(false)}
                    className="text-[10px] tracking-[0.3em] uppercase opacity-60 hover:opacity-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendNote}
                    disabled={noteBusy || !note.trim()}
                    data-testid="mood-note-submit"
                    className="inline-flex items-center gap-2 bg-[hsl(var(--ink))] text-[hsl(var(--bg))] px-5 py-3 text-[11px] tracking-[0.3em] uppercase hover:bg-[hsl(var(--accent))] transition-colors duration-500 disabled:opacity-40"
                  >
                    {noteBusy ? <Loader2 size={12} className="animate-spin" /> : "Send note"}
                  </button>
                </div>
              </div>
            )}

            {noteSent && (
              <div className="pt-4 border-t border-[hsl(var(--line))] text-center py-4 text-sm text-[hsl(var(--ink-soft))]">
                <Check size={20} className="mx-auto text-[hsl(var(--accent))] mb-2" />
                Note sent — thank you.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
